import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { BarChart3, FolderKanban, LogOut, User, Bell, FileText, Clock, Settings, Moon, Sun, Trash2, ChevronDown, Users, Mail, Sparkles, CreditCard, Shield, Lock, Crown, Search, TrendingUp, GitPullRequest, UsersRound, AlertTriangle, Bot, GitBranch, ClipboardList, MessageSquare } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { GlobalTimerWidget, useActiveTimer } from './TimeTrackingWidget';
import NotificationCenter from './NotificationCenter';
import AIAssistant, { openAIAssistantWithProject } from './AIAssistant';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useModules } from '@/contexts/ModuleContext';

const Layout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTimer } = useActiveTimer();
  const { canUseTimeTracking, canUseAIFeatures, features } = useSubscription();
  const { checkModuleAccess, modules } = useModules();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const sendHeartbeat = async () => {
    try {
      await axios.post(`${API}/users/heartbeat`);
    } catch (error) {
      console.error('Heartbeat failed', error);
    }
  };

  useEffect(() => {
    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 60000);
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, []);

  // Check if current path matches any of the given paths
  const isActiveGroup = (paths) => paths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  // Handler to navigate to the active timer's task
  const handleNavigateToTimer = (projectId, taskId) => {
    if (projectId) {
      navigate(`/projects/${projectId}?task=${taskId}`);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-noise">
      <nav className="border-b border-border bg-card/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              {/* Logo */}
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')} data-testid="logo">
                <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Task2Plan
                </span>
              </div>

              {/* Navigation Items */}
              <div className="hidden md:flex items-center gap-1">
                {/* Dashboard - Simple Link */}
                <Button
                  variant={location.pathname === '/' ? 'secondary' : 'ghost'}
                  onClick={() => navigate('/dashboard')}
                  data-testid="nav-dashboard"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>

                {/* Projekte - Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant={isActiveGroup(['/projects', '/templates']) ? 'secondary' : 'ghost'}
                      data-testid="nav-projekte"
                    >
                      <FolderKanban className="w-4 h-4 mr-2" />
                      Projekte
                      <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => navigate('/projects')} className="cursor-pointer">
                      <FolderKanban className="w-4 h-4 mr-2" />
                      Projektübersicht
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/templates')} className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" />
                      Vorlagen
                    </DropdownMenuItem>
                    
                    {/* Dynamic modules in dropdown_projekte - exclude resource_planning (now in Mitarbeiter) */}
                    {modules.filter(m => m.nav_position === 'dropdown_projekte' && m.id !== 'resource_planning' && (m.is_enabled || m.show_teaser)).map(mod => {
                      const access = checkModuleAccess(mod.id);
                      // Use the same route logic as main navigation
                      const routePath = mod.id === 'ai_research' ? '/research' : 
                                       mod.id === 'ai_business_case' ? '/business-case' : 
                                       mod.id === 'change_management' ? '/change-management' :
                                       mod.id === 'resource_planning' ? '/resource-planning' :
                                       mod.id === 'risk_management' ? '/risk-management' :
                                       `/${mod.id.replace('_', '-')}`;
                      
                      // Render appropriate icon based on module icon name
                      const renderIcon = () => {
                        switch(mod.icon) {
                          case 'FileText': return <FileText className="w-4 h-4 mr-2" />;
                          case 'GitBranch': return <GitBranch className="w-4 h-4 mr-2" />;
                          case 'AlertTriangle': return <Shield className="w-4 h-4 mr-2" />; // Keep Shield for risk management per user request
                          case 'TrendingUp': return <TrendingUp className="w-4 h-4 mr-2" />;
                          case 'Bot': return <Bot className="w-4 h-4 mr-2" />;
                          case 'GitPullRequest': return <GitPullRequest className="w-4 h-4 mr-2" />;
                          case 'Users': return <UsersRound className="w-4 h-4 mr-2" />;
                          case 'Search': return <Search className="w-4 h-4 mr-2" />;
                          case 'Sparkles': return <Sparkles className="w-4 h-4 mr-2" />;
                          case 'Shield': return <Shield className="w-4 h-4 mr-2" />;
                          default: return <FolderKanban className="w-4 h-4 mr-2" />; // Fallback icon
                        }
                      };

                      // Special handling for ai_assistant - opens chatbot instead of navigating
                      const handleClick = () => {
                        if (mod.id === 'ai_assistant') {
                          // Open chatbot without project context (general mode)
                          openAIAssistantWithProject(null, null);
                        } else {
                          navigate(routePath);
                        }
                      };
                      
                      return (
                        <DropdownMenuItem 
                          key={mod.id}
                          onClick={handleClick} 
                          className="cursor-pointer"
                        >
                          {renderIcon()}
                          {mod.name}
                          {!mod.is_enabled && mod.show_teaser && (
                            <Badge variant="secondary" className="ml-auto text-xs py-0 px-1">Bald</Badge>
                          )}
                          {mod.is_enabled && !access.hasAccess && (
                            <Lock className="w-3 h-3 ml-auto text-amber-500" />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Dynamic modules in main navigation - exclude resource_planning (now in Mitarbeiter) */}
                {modules.filter(m => m.nav_position === 'main' && m.id !== 'resource_planning' && (m.is_enabled || m.show_teaser)).map(mod => {
                  const access = checkModuleAccess(mod.id);
                  const routePath = mod.id === 'ai_research' ? '/research' : 
                                   mod.id === 'ai_business_case' ? '/business-case' : 
                                   mod.id === 'change_management' ? '/change-management' :
                                   mod.id === 'resource_planning' ? '/resource-planning' :
                                   `/${mod.id.replace('_', '-')}`;
                  
                  // Render appropriate icon based on module icon name
                  const renderMainIcon = () => {
                    switch(mod.icon) {
                      case 'Search': return <Search className="w-4 h-4 mr-2" />;
                      case 'TrendingUp': return <TrendingUp className="w-4 h-4 mr-2" />;
                      case 'Bot': return <Bot className="w-4 h-4 mr-2" />;
                      case 'FileText': return <FileText className="w-4 h-4 mr-2" />;
                      case 'GitPullRequest': return <GitPullRequest className="w-4 h-4 mr-2" />;
                      case 'Users': return <UsersRound className="w-4 h-4 mr-2" />;
                      case 'AlertTriangle': return <Shield className="w-4 h-4 mr-2" />;
                      case 'Sparkles': return <Sparkles className="w-4 h-4 mr-2" />;
                      case 'Shield': return <Shield className="w-4 h-4 mr-2" />;
                      case 'GitBranch': return <GitBranch className="w-4 h-4 mr-2" />;
                      default: return <FolderKanban className="w-4 h-4 mr-2" />;
                    }
                  };
                  
                  return (
                    <Button
                      key={mod.id}
                      variant={location.pathname === routePath ? 'secondary' : 'ghost'}
                      onClick={() => navigate(routePath)}
                      data-testid={`nav-${mod.id}`}
                      className="relative"
                    >
                      {renderMainIcon()}
                      {mod.name}
                      {!mod.is_enabled && mod.show_teaser && (
                        <Badge variant="secondary" className="ml-2 text-xs py-0 px-1.5">Bald</Badge>
                      )}
                      {mod.is_enabled && !access.hasAccess && (
                        <Lock className="w-3 h-3 ml-1 text-amber-500" />
                      )}
                    </Button>
                  );
                })}

                {/* Mitarbeiter - Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant={isActiveGroup(['/time-tracking', '/resource-planning']) ? 'secondary' : 'ghost'}
                      data-testid="nav-mitarbeiter"
                      className={`relative ${activeTimer ? 'ring-2 ring-red-500/50 ring-offset-1' : ''}`}
                    >
                      <Users className={`w-4 h-4 mr-2 ${activeTimer ? 'text-red-500' : ''}`} />
                      Mitarbeiter
                      <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
                      {activeTimer && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => navigate('/time-tracking')} className="cursor-pointer">
                      <Clock className={`w-4 h-4 mr-2 ${activeTimer ? 'text-red-500' : ''}`} />
                      Zeiterfassung
                      {!canUseTimeTracking() && (
                        <Badge variant="outline" className="ml-auto text-[10px] px-1 py-0 h-4 bg-yellow-100 text-yellow-700 border-yellow-300">
                          PRO
                        </Badge>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/resource-planning')} className="cursor-pointer">
                      <UsersRound className="w-4 h-4 mr-2" />
                      Ressourcenplanung
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Tools - Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant={location.pathname.startsWith('/requirements-catalog') ? 'secondary' : 'ghost'}
                      data-testid="nav-tools"
                    >
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Tools
                      <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => navigate('/requirements-catalog')} className="cursor-pointer">
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Anforderungskatalog
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Einstellungen - Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant={location.pathname === '/settings' ? 'secondary' : 'ghost'}
                      data-testid="nav-einstellungen"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Einstellungen
                      <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Einstellungen</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/settings?tab=users')} className="cursor-pointer">
                      <Users className="w-4 h-4 mr-2" />
                      Benutzerverwaltung
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings?tab=ai')} className="cursor-pointer">
                      <Sparkles className="w-4 h-4 mr-2" />
                      KI-Einstellungen
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings?tab=time')} className="cursor-pointer">
                      <Clock className="w-4 h-4 mr-2" />
                      Zeiterfassung
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings?tab=email')} className="cursor-pointer">
                      <Mail className="w-4 h-4 mr-2" />
                      E-Mail-Benachrichtigungen
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings?tab=feedback')} className="cursor-pointer">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Feedback geben
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/subscription')} className="cursor-pointer">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Abonnement
                    </DropdownMenuItem>
                    
                    {/* Dynamic modules in dropdown_settings */}
                    {modules.filter(m => m.nav_position === 'dropdown_settings' && (m.is_enabled || m.show_teaser)).length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs text-muted-foreground">Module</DropdownMenuLabel>
                        {modules.filter(m => m.nav_position === 'dropdown_settings' && (m.is_enabled || m.show_teaser)).map(mod => {
                          const access = checkModuleAccess(mod.id);
                          
                          // Render appropriate icon based on module icon name
                          const renderSettingsIcon = () => {
                            switch(mod.icon) {
                              case 'Search': return <Search className="w-4 h-4 mr-2" />;
                              case 'FileText': return <FileText className="w-4 h-4 mr-2" />;
                              case 'Bot': return <Bot className="w-4 h-4 mr-2" />;
                              case 'AlertTriangle': return <Shield className="w-4 h-4 mr-2" />;
                              case 'GitBranch': return <GitBranch className="w-4 h-4 mr-2" />;
                              case 'TrendingUp': return <TrendingUp className="w-4 h-4 mr-2" />;
                              case 'GitPullRequest': return <GitPullRequest className="w-4 h-4 mr-2" />;
                              case 'Users': return <UsersRound className="w-4 h-4 mr-2" />;
                              case 'Sparkles': return <Sparkles className="w-4 h-4 mr-2" />;
                              case 'Shield': return <Shield className="w-4 h-4 mr-2" />;
                              default: return <FolderKanban className="w-4 h-4 mr-2" />;
                            }
                          };
                          
                          return (
                            <DropdownMenuItem 
                              key={mod.id}
                              onClick={() => navigate(`/${mod.id.replace('_', '-')}`)} 
                              className="cursor-pointer"
                              disabled={!access.hasAccess}
                            >
                              <div className="flex items-center justify-between w-full">
                                <span className="flex items-center">
                                  {renderSettingsIcon()}
                                  {mod.name}
                                </span>
                                {!mod.is_enabled && mod.show_teaser && (
                                  <Badge variant="secondary" className="ml-2 text-xs py-0 px-1">Bald</Badge>
                                )}
                                {mod.is_enabled && !access.hasAccess && (
                                  <Lock className="w-3 h-3 ml-1 text-amber-500" />
                                )}
                              </div>
                            </DropdownMenuItem>
                          );
                        })}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              {/* Papierkorb Icon */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/trash')}
                title="Papierkorb"
                data-testid="nav-trash"
                className={location.pathname === '/trash' ? 'bg-secondary' : ''}
              >
                <Trash2 className="w-5 h-5" />
              </Button>

              {/* Global Timer Widget */}
              <GlobalTimerWidget onNavigateToTimer={handleNavigateToTimer} />
              
              {/* Notification Center */}
              <NotificationCenter />
              
              {/* Dark Mode Toggle */}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={toggleDarkMode}
                title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
                data-testid="dark-mode-toggle"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                    {user?.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="user-menu">
                  <DropdownMenuLabel>Mein Konto</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-muted-foreground">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {user?.role === 'super_admin' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-primary font-medium">
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={onLogout} className="text-destructive" data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {children}
      </main>

      <footer className="border-t border-border mt-auto py-6">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-muted-foreground">
          © 2026 Task2Plan. Alle Rechte vorbehalten.
        </div>
      </footer>
      
      {/* AI Assistant Floating Chat */}
      <AIAssistant />
    </div>
  );
};

export default Layout;
